<template>
  <div class="flex flex-col h-full bg-white">
    <!-- Show skeleton while loading -->
    <FAQDetailSkeleton v-if="isLoading" />
    
    <!-- Actual content when loaded -->
    <template v-else>
      <!-- Header -->
      <div class="px-4 py-3 bg-white sticky top-0 z-10" 
           :style="{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }">
        <div class="flex justify-between items-center">
          <!-- Back button -->
          <button 
            @click="goBack" 
            class="cursor-pointer text-neutral-600 hover:text-neutral-800 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <!-- Center - FAQ Detail title -->
          <div class="flex items-center">
            <h2 class="text-lg font-medium text-neutral-800">FAQ Details</h2>
          </div>
          
          <!-- Expand/Collapse icon -->
          <button 
            @click="expandFAQ" 
            class="cursor-pointer text-neutral-600 hover:text-neutral-800 transition-colors"
          >
            <!-- Expand icon (when not expanded) -->
            <svg v-if="!isExpanded" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
            </svg>
            <!-- Close/X icon (when expanded) -->
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      <!-- FAQ Content -->
      <div class="flex-1 overflow-y-auto pb-20">
        <!-- No FAQ selected state -->
        <div v-if="!faq" class="flex flex-col items-center justify-center py-8 text-neutral-500 h-full">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="text-lg font-medium">No FAQ Selected</p>
          <button 
            @click="goBack" 
            class="mt-4 px-4 py-2 text-sm bg-neutral-800 text-white rounded-md hover:bg-neutral-900"
          >
            Back to FAQ List
          </button>
        </div>
        
        <!-- FAQ article format -->
        <div v-else class="px-4 py-4 mx-auto">
          <!-- Question as Title -->
          <h1 class="text-2xl font-bold text-neutral-900 mb-3">{{ faq.question }}</h1>
          
          <!-- Excerpt under the title -->
          <div v-if="faq.excerpt" class="mb-6 text-neutral-600 text-base leading-relaxed">
            {{ faq.excerpt }}
          </div>
          
          <!-- Left-aligned Author Info Section -->
          <div v-if="hasAuthorInfo" class="flex items-start space-x-3 mb-6">
            <!-- Author Avatar -->
            <div class="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
                 :style="{ border: '1px solid #d1d5db' }">
              <img 
                v-if="getAuthorImage()" 
                :src="getAuthorImage()" 
                :alt="getAuthorName()" 
                class="w-full h-full object-cover" 
              />
              <div 
                v-else 
                class="w-full h-full flex items-center justify-center text-white text-xs font-medium"
                :style="{ backgroundColor: getAuthorColor() }"
              >
                {{ getAuthorInitials() }}
              </div>
            </div>
            
            <!-- Author Details -->
            <div class="flex flex-col">
              <!-- Name and Author label in one line -->
              <div class="flex items-center space-x-2 text-sm">
                <span class="font-medium text-neutral-800">{{ getAuthorName() }}</span>
                <span class="text-xs text-neutral-500">• Autor</span>
              </div>
              
              <!-- Last Updated beneath -->
              <div v-if="faq.last_updated" class="text-xs text-neutral-400 mt-0.5">
                {{ formatDate(faq.last_updated) }}
              </div>
            </div>
          </div>
          
          <!-- Content area for rich text - WITH ENHANCED STYLING -->
          <div class="faq-content">
            <!-- If content is HTML from rich text editor -->
            <div v-if="isHtml(faq.answer)" v-html="faq.answer"></div>
            
            <!-- Fallback for plain text -->
            <p v-else class="text-neutral-700 whitespace-pre-line">{{ faq.answer }}</p>
          </div>
          
          <!-- Feedback section with real emojis -->
          <div class="mt-10 pt-6" 
               :style="{ borderTop: '1px solid #e5e7eb' }">
            <div class="text-center">
              <p class="text-sm text-neutral-600 mb-4">Wie war dieser Artikel?</p>
              <div class="flex justify-center space-x-8">
                <!-- Happy/Helpful -->
                <button 
                  @click="markHelpful('happy')" 
                  class="flex flex-col items-center cursor-pointer hover:bg-neutral-50 transition p-2 rounded-lg"
                >
                  <span class="text-3xl mb-1">😊</span>
                  <span class="text-xs text-neutral-600">Hilfreich</span>
                </button>
                
                <!-- Neutral -->
                <button 
                  @click="markHelpful('neutral')" 
                  class="flex flex-col items-center cursor-pointer hover:bg-neutral-50 transition p-2 rounded-lg"
                >
                  <span class="text-3xl mb-1">😐</span>
                  <span class="text-xs text-neutral-600">Neutral</span>
                </button>
                
                <!-- Unhappy/Not Helpful -->
                <button 
                  @click="markHelpful('unhappy')" 
                  class="flex flex-col items-center cursor-pointer hover:bg-neutral-50 transition p-2 rounded-lg"
                >
                  <span class="text-3xl mb-1">😕</span>
                  <span class="text-xs text-neutral-600">Nicht gut</span>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Related articles -->
          <div v-if="relatedFAQs.length > 0" class="mt-10 pt-6" 
               :style="{ borderTop: '1px solid #e5e7eb' }">
            <h3 class="text-lg font-medium text-neutral-800 mb-3">Ähnliche Artikel</h3>
            
            <div class="space-y-3">
              <div 
                v-for="relatedFaq in relatedFAQs" 
                :key="relatedFaq.$id"
                class="p-4 rounded-md cursor-pointer hover:bg-neutral-50"
                :style="{ border: '1px solid #e5e7eb' }"
                @click="viewFAQ(relatedFaq)"
              >
                <p class="font-medium text-neutral-800">{{ relatedFaq.question }}</p>
                <p v-if="relatedFaq.excerpt" class="text-sm text-neutral-600 mt-1 line-clamp-2">
                  {{ relatedFaq.excerpt }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
  
<script>
import { fetchFAQsByCategory } from '../../lib/appwriteFaq';
import FAQDetailSkeleton from '../../utils/FAQDetailSkeleton.vue';

export default {
  name: 'FAQDetailScreen',
  components: {
    FAQDetailSkeleton
  },
  props: {
    faq: {
      type: Object,
      default: null
    },
    isExpanded: {
      type: Boolean,
      default: false
    }
  },
  emits: ['go-back', 'view-faq', 'change-screen', 'expand-faq'],
  data() {
    return {
      relatedFAQs: [],
      isLoading: false,
      contentLoaded: false
    };
  },
  computed: {
    // Check if FAQ has any author information
    hasAuthorInfo() {
      if (!this.faq) return false;
      
      // Check for new author fields OR legacy author fields
      return !!(
        this.faq.authorName || 
        this.faq.author || 
        this.faq.authorEmail ||
        this.faq.authorImage
      );
    }
  },
  watch: {
    // Watch for changes in the faq prop
    faq(newFaq, oldFaq) {
      if (newFaq && newFaq !== oldFaq) {
        this.loadFAQContent();
      }
    }
  },
  mounted() {
    if (this.faq) {
      this.loadFAQContent();
    }
  },
  methods: {
    async loadFAQContent() {
      console.log('📄 Loading FAQ content...');
      this.isLoading = true;
      this.contentLoaded = false;
      
      try {
        // Simulate minimum loading time for better UX
        const [_, relatedFAQs] = await Promise.all([
          new Promise(resolve => setTimeout(resolve, 500)), // Minimum 500ms loading
          this.loadRelatedFAQs()
        ]);
        
        // Apply styles after content loads
        await this.$nextTick();
        this.applyInlineStyles();
        
        this.contentLoaded = true;
        console.log('✅ FAQ content loaded');
        
      } catch (error) {
        console.error('❌ Error loading FAQ content:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async loadRelatedFAQs() {
      try {
        if (!this.faq || !this.faq.category) {
          this.relatedFAQs = [];
          return;
        }
        
        // Load other FAQs in the same category
        const categoryFAQs = await fetchFAQsByCategory(this.faq.category);
        
        // Filter out the current FAQ
        this.relatedFAQs = categoryFAQs
          .filter(f => f.$id !== this.faq.$id)
          .slice(0, 3); // Limit to 3 related FAQs
          
        console.log('📚 Loaded related FAQs:', this.relatedFAQs.length);
      } catch (error) {
        console.error('Error loading related FAQs:', error);
        this.relatedFAQs = [];
      }
    },
    
    // FIXED: Back button now collapses widget if expanded
    goBack() {
      console.log('📏 FAQ Detail: Going back, current expanded state:', this.isExpanded);
      
      // If widget is expanded, collapse it when going back
      if (this.isExpanded) {
        console.log('📏 FAQ Detail: Collapsing expanded widget on back');
        this.$emit('expand-faq'); // This will toggle the expanded state
      }
      
      // Then go back to the previous screen
      this.$emit('go-back');
    },
    
    viewFAQ(faq) {
      this.$emit('view-faq', faq);
    },
    
    expandFAQ() {
      console.log('📏 FAQ Detail: Expand button clicked');
      this.$emit('expand-faq');
    },
    
    // Get author name with fallback logic
    getAuthorName() {
      if (!this.faq) return 'Unknown Author';
      
      // Try new author field first, then fall back to legacy field
      return this.faq.authorName || this.faq.author || 'Unknown Author';
    },
    
    // Get author image with fallback logic
    getAuthorImage() {
      if (!this.faq) return null;
      
      // Return the author image if it exists
      return this.faq.authorImage || null;
    },
    
    // Get author initials for avatar fallback
    getAuthorInitials() {
      const name = this.getAuthorName();
      if (!name || name === 'Unknown Author') return '?';
      
      return name
        .split(' ')
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },
    
    // Generate a consistent color for author based on their name
    getAuthorColor() {
      const name = this.getAuthorName();
      if (!name || name === 'Unknown Author') return '#6b7280'; // Gray for unknown
      
      // Generate a consistent color based on the author name
      const colors = [
        '#3b82f6', // Blue
        '#10b981', // Green
        '#8b5cf6', // Purple
        '#f59e0b', // Amber
        '#ef4444', // Red
        '#06b6d4', // Cyan
        '#84cc16', // Lime
        '#f97316', // Orange
        '#ec4899', // Pink
        '#6366f1'  // Indigo
      ];
      
      // Create a simple hash from the name
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      
      // Use the hash to pick a color
      const index = Math.abs(hash) % colors.length;
      return colors[index];
    },
    
    formatCategoryName(category) {
      if (!category) return 'General';
      
      // Convert snake_case or kebab-case to Title Case
      return category
        .replace(/[-_]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      
      try {
        const date = new Date(dateString);
        // Format as "Updated 1 month ago" style
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        if (diffDays < 1) {
          return 'Heute aktualisiert';
        } else if (diffDays === 1) {
          return 'Gestern aktualisiert';
        } else if (diffDays < 30) {
          return `Aktualisiert vor ${diffDays} Tagen`;
        } else if (diffDays < 365) {
          const months = Math.floor(diffDays / 30);
          return `Aktualisiert vor ${months} ${months === 1 ? 'Monat' : 'Monaten'}`;
        } else {
          const years = Math.floor(diffDays / 365);
          return `Aktualisiert vor ${years} ${years === 1 ? 'Jahr' : 'Jahren'}`;
        }
      } catch (error) {
        return dateString;
      }
    },
    
    isHtml(str) {
      if (!str) return false;
      return /<[a-z][\s\S]*>/i.test(str);
    },
    
    getInitials(name) {
      if (!name) return '';
      return name
        .split(' ')
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },
    
    markHelpful(reaction) {
      // In the future, you could track this data with different reactions
      const messages = {
        'happy': 'Thank you for your positive feedback!',
        'neutral': 'Thanks for your feedback!',
        'unhappy': 'We\'ll try to improve this article. Thank you for your feedback.'
      };
      
      alert(messages[reaction] || 'Thank you for your feedback!');
    },

    // Apply inline styles for Shadow DOM compatibility
    applyInlineStyles() {
      const contentEl = this.$el?.querySelector('.faq-content');
      if (!contentEl) return;

      // Style headings - SMALLER SIZES, TIGHTER SPACING
      const headings = contentEl.querySelectorAll('h1, h2, h3, h4, h5, h6');
      headings.forEach(heading => {
        const styles = {
          h1: { fontSize: '1.625rem', fontWeight: '700', marginBottom: '1rem', marginTop: '1.5rem', color: '#0a0a0a', lineHeight: '1.3' },
          h2: { fontSize: '1.375rem', fontWeight: '600', marginBottom: '0.875rem', marginTop: '1.25rem', color: '#0a0a0a', lineHeight: '1.35' },
          h3: { fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.75rem', marginTop: '1rem', color: '#0a0a0a', lineHeight: '1.4' },
          h4: { fontSize: '1rem', fontWeight: '600', marginBottom: '0.625rem', marginTop: '0.875rem', color: '#0a0a0a', lineHeight: '1.4' },
          h5: { fontSize: '0.9375rem', fontWeight: '600', marginBottom: '0.5rem', marginTop: '0.75rem', color: '#0a0a0a', lineHeight: '1.4' },
          h6: { fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', marginTop: '0.75rem', color: '#525252', lineHeight: '1.4' }
        };

        const tagName = heading.tagName.toLowerCase();
        if (styles[tagName]) {
          Object.assign(heading.style, styles[tagName]);
          heading.style.display = 'block';
        }

        // First child headings get no top margin
        if (heading.parentElement && heading === heading.parentElement.firstElementChild) {
          heading.style.marginTop = '0';
        }
      });

      // Style paragraphs - LARGER TEXT, BETTER SPACING
      const paragraphs = contentEl.querySelectorAll('p');
      paragraphs.forEach(p => {
        p.style.marginBottom = '1rem';
        p.style.lineHeight = '1.6';
        p.style.color = '#525252';
        p.style.fontSize = '1rem'; // Increased from default
      });

      // Style strong elements
      const strongElements = contentEl.querySelectorAll('strong');
      strongElements.forEach(strong => {
        strong.style.fontWeight = '600';
        strong.style.color = '#0a0a0a';
      });

      // Style em elements
      const emElements = contentEl.querySelectorAll('em');
      emElements.forEach(em => {
        em.style.fontStyle = 'italic';
      });

      // Style u elements
      const uElements = contentEl.querySelectorAll('u');
      uElements.forEach(u => {
        u.style.textDecoration = 'underline';
      });

      // Style blockquotes
      const blockquotes = contentEl.querySelectorAll('blockquote');
      blockquotes.forEach(bq => {
        Object.assign(bq.style, {
          borderLeft: '4px solid #e5e7eb',
          padding: '1rem',
          margin: '1.25rem 0',
          fontStyle: 'italic',
          color: '#737373',
          backgroundColor: '#fafafa',
          borderRadius: '0.25rem',
          display: 'block'
        });
      });

      // Style links
      const links = contentEl.querySelectorAll('a');
      links.forEach(link => {
        link.style.color = '#2563eb';
        link.style.textDecoration = 'underline';
      });

      // Style lists
      const lists = contentEl.querySelectorAll('ol, ul');
      lists.forEach(list => {
        list.style.paddingLeft = '1.5rem';
        list.style.marginBottom = '1rem';
        list.style.display = 'block';
        
        if (list.tagName.toLowerCase() === 'ol') {
          list.style.listStyleType = 'decimal';
        } else {
          list.style.listStyleType = 'disc';
        }
      });

      // Style list items
      const listItems = contentEl.querySelectorAll('li');
      listItems.forEach(li => {
        li.style.marginBottom = '0.375rem';
        li.style.lineHeight = '1.5';
        li.style.display = 'list-item';
        li.style.fontSize = '1rem'; // Consistent with paragraphs

        // Handle Quill's data-list attributes
        if (li.getAttribute('data-list') === 'ordered') {
          li.style.listStyleType = 'decimal';
        } else if (li.getAttribute('data-list') === 'bullet') {
          li.style.listStyleType = 'disc';
        }
      });

      // Style Quill code blocks
      const codeContainers = contentEl.querySelectorAll('.ql-code-block-container');
      codeContainers.forEach(container => {
        Object.assign(container.style, {
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '0.5rem',
          margin: '1.25rem 0',
          overflow: 'hidden',
          display: 'block'
        });
      });

      const codeBlocks = contentEl.querySelectorAll('.ql-code-block');
      codeBlocks.forEach(code => {
        Object.assign(code.style, {
          padding: '1rem',
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", monospace',
          fontSize: '0.875rem',
          color: '#1f2937',
          backgroundColor: '#f8fafc',
          overflowX: 'auto',
          whiteSpace: 'pre',
          display: 'block'
        });
      });

      // Hide Quill UI elements
      const quillUI = contentEl.querySelectorAll('.ql-ui');
      quillUI.forEach(ui => {
        ui.style.display = 'none';
        ui.style.visibility = 'hidden';
      });

      // Style images
      const images = contentEl.querySelectorAll('img');
      images.forEach(img => {
        Object.assign(img.style, {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '0.5rem',
          margin: '1.25rem 0',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          display: 'block'
        });
      });

      console.log('✅ Applied inline styles to FAQ content elements');
    }
  }
};
</script>

<style scoped>
/* Enhanced FAQ Content Styling for Shadow DOM + Quill */
:host .faq-content,
.faq-content {
  line-height: 1.6;
  color: #525252;
}

/* Use ::v-deep for Vue scoped styles in Shadow DOM */
::v-deep .faq-content h1,
.faq-content h1 {
  font-size: 1.625rem !important; /* Reduced from 1.875rem */
  font-weight: 700 !important;
  margin-bottom: 1rem !important; /* Reduced from 1.25rem */
  margin-top: 1.5rem !important; /* Reduced from 2rem */
  color: #0a0a0a !important;
  line-height: 1.3 !important; /* Tighter from 1.2 */
  display: block !important;
}

::v-deep .faq-content h1:first-child,
.faq-content h1:first-child {
  margin-top: 0 !important;
}

::v-deep .faq-content h2,
.faq-content h2 {
  font-size: 1.375rem !important; /* Reduced from 1.5rem */
  font-weight: 600 !important;
  margin-bottom: 0.875rem !important; /* Reduced from 1rem */
  margin-top: 1.25rem !important; /* Reduced from 1.75rem */
  color: #0a0a0a !important;
  line-height: 1.35 !important; /* Tighter from 1.3 */
  display: block !important;
}

::v-deep .faq-content h2:first-child,
.faq-content h2:first-child {
  margin-top: 0 !important;
}

::v-deep .faq-content h3,
.faq-content h3 {
  font-size: 1.125rem !important; /* Reduced from 1.25rem */
  font-weight: 600 !important;
  margin-bottom: 0.75rem !important; /* Reduced from 0.875rem */
  margin-top: 1rem !important; /* Reduced from 1.5rem */
  color: #0a0a0a !important;
  line-height: 1.4 !important;
  display: block !important;
}

::v-deep .faq-content h3:first-child,
.faq-content h3:first-child {
  margin-top: 0 !important;
}

::v-deep .faq-content h4,
.faq-content h4 {
  font-size: 1rem !important; /* Reduced from 1.125rem */
  font-weight: 600 !important;
  margin-bottom: 0.625rem !important; /* Reduced from 0.75rem */
  margin-top: 0.875rem !important; /* Reduced from 1.25rem */
  color: #0a0a0a !important;
  line-height: 1.4 !important;
  display: block !important;
}

::v-deep .faq-content h5,
.faq-content h5 {
  font-size: 0.9375rem !important; /* Reduced from 1rem */
  font-weight: 600 !important;
  margin-bottom: 0.5rem !important;
  margin-top: 0.75rem !important; /* Reduced from 1rem */
  color: #0a0a0a !important;
  line-height: 1.4 !important;
  display: block !important;
}

::v-deep .faq-content h6,
.faq-content h6 {
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  margin-bottom: 0.5rem !important;
  margin-top: 0.75rem !important; /* Reduced from 1rem */
  color: #525252 !important;
  line-height: 1.4 !important;
  display: block !important;
}

/* Paragraphs - LARGER TEXT */
::v-deep .faq-content p,
.faq-content p {
  margin-bottom: 1rem !important; /* Reduced from 1.25rem */
  line-height: 1.6 !important; /* Tighter from 1.7 */
  color: #525252 !important;
  font-size: 1rem !important; /* Explicitly set larger size */
}

::v-deep .faq-content p:last-child,
.faq-content p:last-child {
  margin-bottom: 0 !important;
}

/* Text formatting */
::v-deep .faq-content strong,
.faq-content strong {
  font-weight: 600 !important;
  color: #0a0a0a !important;
}

::v-deep .faq-content em,
.faq-content em {
  font-style: italic !important;
}

::v-deep .faq-content u,
.faq-content u {
  text-decoration: underline !important;
}

/* Blockquotes */
::v-deep .faq-content blockquote,
.faq-content blockquote {
  border-left: 4px solid #e5e7eb !important;
  padding: 1rem !important;
  margin: 1.25rem 0 !important; /* Reduced from 1.5rem */
  font-style: italic !important;
  color: #737373 !important;
  background-color: #fafafa !important;
  border-radius: 0.25rem !important;
  display: block !important;
}

/* Links */
::v-deep .faq-content a,
.faq-content a {
  color: #2563eb !important;
  text-decoration: underline !important;
  transition: color 0.2s !important;
}

::v-deep .faq-content a:hover,
.faq-content a:hover {
  color: #1d4ed8 !important;
}

/* Lists */
::v-deep .faq-content ol,
::v-deep .faq-content ul,
.faq-content ol,
.faq-content ul {
  padding-left: 1.5rem !important;
  margin-bottom: 1rem !important; /* Reduced from 1.25rem */
  display: block !important;
}

::v-deep .faq-content ol,
.faq-content ol {
  list-style-type: decimal !important;
}

::v-deep .faq-content ul,
.faq-content ul {
  list-style-type: disc !important;
}

::v-deep .faq-content li,
.faq-content li {
  margin-bottom: 0.375rem !important; /* Reduced from 0.5rem */
  line-height: 1.5 !important; /* Tighter from 1.6 */
  display: list-item !important;
  font-size: 1rem !important; /* Consistent with paragraphs */
}

::v-deep .faq-content li[data-list="ordered"],
.faq-content li[data-list="ordered"] {
  list-style-type: decimal !important;
}

::v-deep .faq-content li[data-list="bullet"],
.faq-content li[data-list="bullet"] {
  list-style-type: disc !important;
}

/* Quill Code Blocks */
::v-deep .faq-content .ql-code-block-container,
.faq-content .ql-code-block-container {
  background-color: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 0.5rem !important;
  margin: 1.25rem 0 !important; /* Reduced from 1.5rem */
  overflow: hidden !important;
  display: block !important;
}

::v-deep .faq-content .ql-code-block,
.faq-content .ql-code-block {
  padding: 1rem !important;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', monospace !important;
  font-size: 0.875rem !important;
  color: #1f2937 !important;
  background-color: #f8fafc !important;
  overflow-x: auto !important;
  white-space: pre !important;
  display: block !important;
}

/* Hide Quill UI elements */
::v-deep .faq-content .ql-ui,
.faq-content .ql-ui {
  display: none !important;
  visibility: hidden !important;
}

/* Images */
::v-deep .faq-content img,
.faq-content img {
  max-width: 100% !important;
  height: auto !important;
  border-radius: 0.5rem !important;
  margin: 1.25rem 0 !important; /* Reduced from 1.5rem */
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1) !important;
  display: block !important;
}

/* Tables */
::v-deep .faq-content table,
.faq-content table {
  width: 100% !important;
  border-collapse: collapse !important;
  margin: 1.25rem 0 !important; /* Reduced from 1.5rem */
  font-size: 0.875rem !important;
  display: table !important;
}

::v-deep .faq-content th,
::v-deep .faq-content td,
.faq-content th,
.faq-content td {
  border: 1px solid #e5e7eb !important;
  padding: 0.75rem !important;
  text-align: left !important;
}

::v-deep .faq-content th,
.faq-content th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #0a0a0a !important;
}

/* Line clamp utilities */
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #343536;
}

.overflow-y-auto {
  scroll-behavior: smooth;
}
</style>